import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import './QuotePage.css';
import APP_CONFIG, { TIER_ORDER, formatCapacity, getCurrencySymbol } from '../config/appConfig';
import { priceQuote, buildProjections, makeMoneyFormatter } from '../lib/pricing';
import { resolveCalculationSettings } from '../lib/settingsStore';
import { loadQuote } from '../lib/share';
import ProjectionsChart from '../components/ProjectionsChart';
import Logo from '../components/Logo';

/**
 * Customer-facing quote.
 *
 * Read-only by construction: it takes a saved quote state, re-prices it through
 * the same engine the salesperson used, and renders the result. Because pricing
 * is recomputed rather than stored as rendered text, a quote cannot show
 * figures that the current model would not produce.
 */
const QuotePage = () => {
    const { id } = useParams();
    const location = useLocation();
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        loadQuote(id, location.hash)
            .then(data => { if (!cancelled) setPayload(data); })
            .catch(e => { if (!cancelled) setError(e.message); });
        return () => { cancelled = true; };
    }, [id, location.hash]);

    // Re-price on the calibration the quote was created with, not on whatever
    // the team's settings happen to be now — a sent quote must not move.
    const priced = useMemo(() => {
        if (!payload) return null;
        const pinned = payload.meta?.calculationSettings;
        return priceQuote(payload.state, pinned ? resolveCalculationSettings(pinned) : undefined);
    }, [payload]);
    const projections = useMemo(
        () => (priced ? buildProjections({
            revenue: priced.revenue,
            tierCosts: priced.tierCosts,
            monthlyGrowthRate: priced.state.monthlyGrowthRate
        }) : null),
        [priced]
    );

    if (error) {
        return (
            <div className="quote-page">
                <div className="quote-shell quote-error">
                    <h1>We couldn't open this quote</h1>
                    <p>{error}</p>
                    <p className="quote-error-help">
                        Ask your inoRain contact to send a fresh link.
                    </p>
                </div>
            </div>
        );
    }

    if (!priced) {
        return (
            <div className="quote-page">
                <div className="quote-shell"><p className="quote-loading">Loading your quote…</p></div>
            </div>
        );
    }

    const meta = payload.meta || {};
    const { tierCosts, recommendedTier, requirements, activeSubscribers, state } = priced;
    const chosen = tierCosts[recommendedTier];
    const pkg = APP_CONFIG.packages[recommendedTier];
    const symbol = getCurrencySymbol(state.currency);

    const money = makeMoneyFormatter(state.currency);

    const yearly = chosen.isCustom
        ? null
        : chosen.monthlyRecurringCost * 12 * (1 - APP_CONFIG.billing.yearlyDiscount);

    const includedPlatforms = Object.entries(state.selectedPlatforms || {})
        .filter(([k, on]) => on && APP_CONFIG.platforms[k])
        .map(([k]) => APP_CONFIG.platforms[k]);

    const includedFeatures = Object.entries(state.selectedFeatures || {})
        .filter(([k, on]) => on && APP_CONFIG.features[k])
        .map(([k]) => APP_CONFIG.features[k]);

    return (
        <div className="quote-page">
            <header className="quote-masthead">
                <div className="quote-brand">
                    <Logo height={26} />
                    <span className="quote-tag">OTT Platform</span>
                </div>
                {meta.preparedBy && <span className="quote-by">Prepared by {meta.preparedBy}</span>}
            </header>

            <div className="quote-shell">
                <div className="quote-intro">
                    <h1>
                        {meta.companyName
                            ? `Your OTT platform quote, ${meta.companyName}`
                            : 'Your OTT platform quote'}
                    </h1>
                    {meta.clientName && <p className="quote-hello">Prepared for {meta.clientName}</p>}
                    {meta.note && <p className="quote-note">{meta.note}</p>}
                </div>

                <section className="quote-headline">
                    <span className="headline-eyebrow">Recommended plan</span>
                    <h2>{pkg.name}</h2>
                    <p className="headline-price">
                        {chosen.isCustom
                            ? 'Custom quote'
                            : <>{money(chosen.monthlyRecurringCost)}<span>/month</span></>}
                    </p>
                    {yearly !== null && (
                        <p className="headline-yearly">
                            or {money(yearly)}/year — a {Math.round(APP_CONFIG.billing.yearlyDiscount * 100)}% saving
                        </p>
                    )}
                    <p className="headline-sub">No upfront cost. Apps and features are included in the plan.</p>
                </section>

                <section className="quote-block">
                    <h3>Sized for your service</h3>
                    <div className="spec-grid">
                        <div className="spec">
                            <span className="spec-label">Catalogue</span>
                            <span className="spec-value">{Number(state.videoAssets).toLocaleString()} titles</span>
                            <span className="spec-note">~{state.avgDuration} min each</span>
                        </div>
                        <div className="spec">
                            <span className="spec-label">Storage</span>
                            <span className="spec-value">{formatCapacity(requirements.storage)}</span>
                            <span className="spec-note">{formatCapacity(pkg.storage)} in plan</span>
                        </div>
                        <div className="spec">
                            <span className="spec-label">Bandwidth</span>
                            <span className="spec-value">{formatCapacity(requirements.bandwidth)}/mo</span>
                            <span className="spec-note">{formatCapacity(pkg.bandwidth)} in plan</span>
                        </div>
                        <div className="spec">
                            <span className="spec-label">Active subscribers</span>
                            <span className="spec-value">{activeSubscribers.toLocaleString()}</span>
                            <span className="spec-note">
                                {pkg.activeSubscribers === Infinity ? 'unlimited' : `~${pkg.activeSubscribers.toLocaleString()} guidance`}
                            </span>
                        </div>
                    </div>
                </section>

                <section className="quote-block">
                    <h3>Your monthly price</h3>
                    <ul className="price-lines">
                        <li>
                            <span>{pkg.name} plan</span>
                            <b>{chosen.isCustom ? 'Contact us' : money(chosen.baseCost)}</b>
                        </li>
                        {chosen.platformAddOnItems.length > 0 && (
                            <li>
                                <span>Additional platforms
                                    <small>{chosen.platformAddOnItems.map(i => i.name).join(', ')}</small>
                                </span>
                                <b>{money(chosen.platformAddOnCost)}</b>
                            </li>
                        )}
                        {chosen.featureAddOnItems.length > 0 && (
                            <li>
                                <span>Additional features
                                    <small>{chosen.featureAddOnItems.map(i => i.name).join(', ')}</small>
                                </span>
                                <b>{money(chosen.featureAddOnCost)}</b>
                            </li>
                        )}
                        {chosen.usageItems.map(item => (
                            <li key={item.key}>
                                <span>{item.name}<small>{item.detail}</small></span>
                                <b>{money(item.cost)}</b>
                            </li>
                        ))}
                        {chosen.overageCost > 0 && (
                            <li>
                                <span>Capacity beyond the plan
                                    <small>
                                        {Math.round(chosen.bandwidthOverGB).toLocaleString()} GB transfer,{' '}
                                        {Math.round(chosen.storageOverGB).toLocaleString()} GB storage
                                    </small>
                                </span>
                                <b>{money(chosen.overageCost)}</b>
                            </li>
                        )}
                        <li className="price-total">
                            <span>Total per month</span>
                            <b>{chosen.isCustom ? 'Contact us' : money(chosen.monthlyRecurringCost)}</b>
                        </li>
                    </ul>
                </section>

                <section className="quote-block">
                    <h3>What's included</h3>
                    <div className="included-cols">
                        <div>
                            <h4>Apps</h4>
                            <ul className="tick-list">
                                {includedPlatforms.map(p => <li key={p.name}>{p.name}</li>)}
                            </ul>
                        </div>
                        <div>
                            <h4>Every plan includes</h4>
                            <ul className="tick-list">
                                {Object.values(APP_CONFIG.features).filter(f => f.core).map(f => (
                                    <li key={f.name}>{f.name}</li>
                                ))}
                            </ul>
                        </div>
                        {includedFeatures.length > 0 && (
                            <div>
                                <h4>Also on your plan</h4>
                                <ul className="tick-list">
                                    {includedFeatures.map(f => <li key={f.name}>{f.name}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>

                <section className="quote-block">
                    <h3>Plan comparison</h3>
                    <div className="quote-table-scroll">
                        <table className="quote-tiers">
                            <thead>
                                <tr>
                                    <th>Plan</th><th>Bandwidth</th><th>Storage</th><th>Subscribers</th><th>Per month</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TIER_ORDER.map(t => (
                                    <tr key={t} className={t === recommendedTier ? 'chosen' : ''}>
                                        <th scope="row">
                                            {APP_CONFIG.packages[t].name}
                                            {t === recommendedTier && <span className="chosen-tag">Your plan</span>}
                                        </th>
                                        <td>{formatCapacity(APP_CONFIG.packages[t].bandwidth)}</td>
                                        <td>{formatCapacity(APP_CONFIG.packages[t].storage)}</td>
                                        <td>{APP_CONFIG.packages[t].activeSubscribers === Infinity
                                            ? 'Custom' : `~${APP_CONFIG.packages[t].activeSubscribers.toLocaleString()}`}</td>
                                        <td className="num">{tierCosts[t].isCustom
                                            ? 'Contact us' : money(tierCosts[t].monthlyRecurringCost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {priced.revenue > 0 && projections && (
                    <section className="quote-block">
                        <h3>Your business case</h3>
                        <p className="block-lead">
                            Based on the {state.revenueModel} model we discussed, growing{' '}
                            {state.monthlyGrowthRate}% per month.
                        </p>
                        <ProjectionsChart
                            projections={projections}
                            currencySymbol={symbol}
                            monthlyGrowthRate={state.monthlyGrowthRate}
                        />
                    </section>
                )}

                <footer className="quote-footer">
                    <p>
                        Prices in {state.currency}. Bandwidth and storage beyond the plan are billed per GB
                        ({money(APP_CONFIG.overage.bandwidth.pricePerGB)}/GB transfer,{' '}
                        {money(APP_CONFIG.overage.storage.pricePerGB)}/GB storage). Approximate active subscribers is a rough guide
                        based on typical usage; real capacity varies with video quality and watch time.
                    </p>
                    {meta.createdAt && (
                        <p className="quote-date">
                            Prepared {new Date(meta.createdAt).toLocaleDateString()}
                        </p>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default QuotePage;
